import { Box, Hidden } from "@mui/material"
import { useStyles } from "./styles"
import { DetailProps, DetailVisitState, ResendState } from "./type"
import { BreadCrumb, SnackSimple } from "../../../../components"
import { createRef, useEffect, useMemo, useState } from "react"
import { CustomPaper } from "../../../../components/CustomPaper"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { actionPlanFetch, userGroupServiceFetch, userScheduleServiceFetch } from "../../../../utils/constants/callService"
import { formatResponse, formatScheduleByQuestionnaireResponse } from "./methods/formatResponse"
import { useTranslation } from "react-i18next"
import { detailVisit, listPaths } from "./constants"
import { Resend } from "../../../../components/resend"
import { Status, Types } from "../../../../utils/enums"
import { Item, ObjectGeneric } from "../../../../utils/types/generals"
import { handleAllErrors } from "../../../../utils/methods/handleErrors"
import { ResendRef } from "../../../../components/resend/types"
import { ModalState, VisitListData } from "../../../calendar/pages/detail/components/visitList/types"
import { BodySection } from "../../../calendar/pages/detail/components/bodySection/types"
import { SectionProps } from "../../../calendar/pages/detail/components/section/types"
import { VisitData, VisitProps } from "../../../calendar/pages/detail/components/visite/type"
import { SubmitResendReport } from "../../../calendar/pages/main/types"
import { resendReportTypes } from "../../../calendar/pages/main/constants/typeResendReport"
import { VisitList } from "../../../calendar/pages/detail/components/visitList"
import { DetailVisit } from "../../../calendar/pages/detail/components/detailVisit"
import { BosySecton } from "../../../calendar/pages/detail/components/bodySection"
import Loader from "../../../../components/loader"
import { DataFlag } from "../../../../utils/enums/dataExistence"
import { EventQuery } from "../../../../hooks/emitter/types"
import { useQuery } from "@tanstack/react-query"

function Detail(props: DetailProps) {
  const ref = createRef<ResendRef>()
  const { id } = useParams()
  const { t } = useTranslation()
  const navigation = useNavigate()
  const styles = useStyles()
  const location = useLocation()

  const [detail, setDetail] = useState<DetailVisitState>()
  const [visitList, setVisitList] = useState<VisitListData>({ from: "", to: "", reportUrl: null, visitList: [] })
  const [bodySection, setBodySection] = useState<BodySection>()
  const [questions, setQuestions] = useState<Array<SectionProps>>()
  const [errorsResend, setErrorsResend] = useState<string[]>([])
  const [openSnackbar, setOpenSnackbar] = useState({ open: false, message: "", type: Types.SUCCESS as string })
  const [modal, setModal] = useState<ModalState>({ loading: false, open: false, title: "" })
  const [resend, setResend] = useState<ResendState>()
  const [dataFlag, setDataFlag] = useState<DataFlag>(DataFlag.LOADING)

  const [userGroup, setUserGroup] = useState<Item[]>([])

  const [logbookPrefilters, setLogbookPrefilters] = useState<{ userId?: string; roleId?: string }>({})
  const [hasActivities, setHasActivities] = useState<boolean | undefined>()
  const [userQuestionnaireIds, setUserQuestionnaireIds] = useState<string | undefined>(undefined)
  const [presetDestination, setPresetDestination] = useState<any>(null)
  const [createUserId, setCreateUserId] = useState<string | number | null>(null)
  const [questionnaireName, setQuestionnaireName] = useState<string>("")

  const searchParams = new URLSearchParams(location.search)
  const idFolio = searchParams.get("idFolio")

  type LogbookNavState = {
    activityName?: string
    assignedQuestionnaireId?: string | number
  }
  const navState = (location.state ?? {}) as LogbookNavState
  const activityNameFromState = (navState.activityName ?? "").trim()

  const seeDetail: VisitProps["seeDetail"] = ({ id: localeId, visitListExternal }) => {
    const list = visitListExternal || visitList.visitList
    const visitFound = list?.find((item) => item.id == localeId)

    setDetail({ preview: true, visitData: visitFound })
    setBodySection((prev) => {
      if (!prev) return undefined
      return {
        ...prev,
        reportData: {
          infoReport: visitFound?.infoReport,
          lastReport: visitFound?.lastReport,
          report: visitFound?.report,
        },
        questionId: localeId,
        status: visitFound?.status ?? Status.INCOMPLETE,
      }
    })

    getQuestionarieById({ localeId, visitData: visitFound })
  }

  const goList = () => {
    navigation("/dashboard/schedule-user/")
  }

  const seeList = () => {
    setDetail({ preview: false })
    setBodySection((prev) => {
      if (!prev) return undefined
      return {
        ...prev,
        reportData: {
          report: {
            date: visitList.from,
            url: visitList.reportUrl,
          },
        },
      }
    })
  }

  const lists = useMemo(() => {
    if (detail?.preview) return detailVisit({ seeList, goList })
    return listPaths({ goList })
  }, [detail?.preview])

  useQuery({
    queryKey: [EventQuery.user_schedule],
    queryFn: () => {
      getDetailById()
      return false
    },
    initialData: false,
    enabled: !!id,
    refetchOnWindowFocus: false,
  })

  const buildPresetDestinationFromStore = (store: any) => {
    const sid = store?.id ?? store
    if (!sid) return null

    return {
      id: sid,
      name: store?.name ?? "",
      latitude: store?.latitude ?? store?.lat ?? "",
      longitude: store?.longitude ?? store?.lng ?? "",
      type: store?.type ?? "UNITY",
      timeZone: store?.timeZone ?? store?.timezone ?? null,
      store: typeof store === "object" ? store : undefined,
    }
  }

  const getDetailById = async () => {
    if (!id) return

    setDataFlag(DataFlag.LOADING)
    const response: any = await userScheduleServiceFetch.getDetail({ id })

    if (response?.errors) {
      setDataFlag(DataFlag.NODATA)
      return
    }

    const qName =
      response?.questionnaireName ??
      response?.assignedQuestionnaires?.[0]?.questionnaireMainData?.name ??
      response?.questionnaireMainData?.name ??
      ""

    setQuestionnaireName(String(qName ?? ""))

    const store = response?.store ?? null

    setHasActivities(Boolean(response?.hasActivities))
    setUserQuestionnaireIds(response?.id !== null && response?.id !== undefined ? String(response.id) : undefined)
    setPresetDestination(buildPresetDestinationFromStore(store))
    setCreateUserId(response?.userAssign?.id?.toString?.() ?? response?.userAssign?.id ?? null)

    setLogbookPrefilters({
      userId: response?.userAssign?.id?.toString?.(),
      roleId: response?.userAssign?.role?.id?.toString?.(),
    })

    const { visitList, bodySection } = formatResponse({ response, t })
    setVisitList(visitList)
    setBodySection(bodySection as BodySection)

    const firstId = visitList?.visitList?.[0]?.id
    if (firstId) seeDetail({ id: firstId, visitListExternal: visitList.visitList })

    setDataFlag(DataFlag.DATA)
  }

  const getQuestionarieById = async ({ localeId, visitData }: { localeId: string | number; visitData?: VisitData }) => {
    if (!id) return
    const response = await userScheduleServiceFetch.getQuestionarieById(localeId)
    setQuestions(
      formatScheduleByQuestionnaireResponse({
        response,
        header: {
          title: visitData?.title ?? "",
          parentId: visitData?.detailVersion?.parentId || null,
          version: visitData?.detailVersion?.version || "1",
        },
      })
    )
  }

  const openResend = (payload?: ResendState) => {
    setModal((prev) => ({ ...prev, open: true }))
    setResend(payload)
  }

  const closeModal = () => {
    setErrorsResend([])
    setModal({ loading: false, open: false, title: "" })
    ref.current?.clearData()
  }

  const submitResendReport = async ({ id, data, questionnaireOption, type }: SubmitResendReport) => {
    const clearData = ref.current?.clearData
    setModal((prev) => ({ ...prev, loading: true }))

    const emails = data?.externalEmails?.split(",")?.filter((item: string) => !!item?.trim()) ?? []

    let body: ObjectGeneric = {
      users_direct_parents: !!data?.users_direct_parents,
      user_creator: !!data?.user_creator,
      user_assign: !!data?.user_assign,
      type: type ?? resendReportTypes.report,
    }

    if (emails.length) body["external_emails"] = emails

    const selectedGroups: any[] = (data as any)?.selectGroup ?? []
    if (selectedGroups.length) {
      body["user_groups"] = selectedGroups
        .map((g: any) => g?.value ?? g?.id ?? g)
        .filter((v: any) => v !== undefined && v !== null)
    }

    const localeId = questionnaireOption?.id ?? id
    const endpointResend = actionPlanFetch.sendReportScheduleUser
    const response = await endpointResend(localeId, body)

    setModal((prev) => ({ ...prev, loading: false }))

    if (!response?.errors) {
      closeModal()
      setOpenSnackbar({ open: true, type: Types.SUCCESS, message: t("general.reportSuccess") })
      if (clearData) clearData()
      return
    }

    if (["00", "01", "02"].includes(response.code)) {
      if (response.code == "00") {
        closeModal()
        setOpenSnackbar({ open: true, type: Types.SUCCESS, message: t("general.reportSuccess") })
        return
      }

      if (response.code == "01") {
        const listErrors: string[] = []

        if (response.data?.userAssign) listErrors.push(t(`reportFolio.modal.sendReport.userWhoResponded`))
        if (response.data?.usersSupervisors) listErrors.push(t(`reportFolio.modal.sendReport.userSupervisor`))
        if (response.data?.usersStore) listErrors.push(t(`reportFolio.modal.sendReport.userStore`))
        if (response.data?.userManageStore) listErrors.push(t(`reportFolio.modal.sendReport.agentStore`))
        if (response.data?.emailStore) listErrors.push(t(`reportFolio.modal.sendReport.emailStore`))
        if (response.data?.externalEmails?.length)
          listErrors.push(`${t(`reportFolio.modal.sendReport.externalEmails`)}: ${response.data?.externalEmails?.join(", ")}`)

        setErrorsResend(listErrors)
        return
      }

      if (response.code == "02") {
        setOpenSnackbar({ open: true, type: Types.ERROR, message: t("general.reportError") })
        return
      }
      return
    }

    if (response.errors?.length) {
      setOpenSnackbar({
        open: true,
        type: Types.ERROR,
        message: handleAllErrors(response?.errors[0].code, t, t("general.reportError")),
      })
    }
  }

  const closeSnackbar = () => {
    setOpenSnackbar((prev) => ({ ...prev, open: false }))
  }

  const saveReportUrl = ({ url }: { url: string }) => {
    setBodySection((prev) => ({
      ...prev,
      reportData: {
        ...(prev?.reportData ?? {}),
        report: {
          ...(prev?.reportData?.report ?? {}),
          url,
        },
      },
    }))
  }

  const getUserGroup = async () => {
    const response: any = await userGroupServiceFetch.getUserGroupSelect()
    if (!response?.errors) {
      setUserGroup(
        response.map((item: any) => ({
          label: item.name,
          value: item.id,
        }))
      )
    } else {
      setUserGroup([])
    }
  }

  useEffect(() => {
    getUserGroup()
  }, [])

  const getFolioBreadcrumbs = () => [
    { id: "maintenanceRequest", label: "maintenanceRequest", navigate: () => navigation(`/dashboard/report-folio/dashboard`) },
    {
      id: "detailReportFolio",
      label: "detailReportFolio",
      navigate: () => navigation(`/dashboard/report-folio/dashboard/detail/${idFolio}`),
    },
    { id: "scheduleDetail", label: "scheduleDetail", navigate: () => {} },
  ]

  const getDefaultBreadcrumbs = () => {
    if ((location.state as any)?.listBreadCrumb) return (location.state as any).listBreadCrumb
    if (detail?.preview) return detailVisit({ seeList, goList })
    return listPaths({ goList })
  }

  const getLogbookBreadcrumbs = () => [
    {
      id: "logbook",
      label: "logbookWithname",
      labelParams: { name: activityNameFromState },
      active: false,
      path: "/dashboard/logbook",
      navigate: () => navigation("/dashboard/logbook", {}),
    },
    {
      id: "scheduleDetail",
      label: "scheduleDetail",
      active: true,
      path: `/dashboard/schedule-user/detail/${id}`,
      navigate: () => {},
    },
  ]

  const getBreadcrumbConfig = () => {
    if (activityNameFromState) {
      return {
        breadList: getLogbookBreadcrumbs(),
        notAddPath: true,
        removeBreadcrumb: ["report-folio", "calendar", "schedule-user"],
      }
    }

    if (idFolio) {
      return {
        breadList: getFolioBreadcrumbs(),
        removeBreadcrumb: ["report-folio", "calendar"],
        replaceLabels: {
          dashboard: "maintenanceRequest",
          scheduleDetail: "reportFolioDetail",
          detail: "visitDetail",
        },
      }
    }

    return {
      breadList: getDefaultBreadcrumbs(),
      removeBreadcrumb: (location.state as any)?.removeBreadcrumb || ["report-folio", "calendar", "schedule-user"],
      replaceLabels: (location.state as any)?.replaceLabels || {
        dashboard: "maintenanceRequest",
        detailEdit: "detailReportFolio",
      },
    }
  }

  return (
    <Box sx={styles.body({ detail: !!detail?.preview })}>
      <Box sx={styles.container}>
        <BreadCrumb {...getBreadcrumbConfig()} />

        {dataFlag === DataFlag.LOADING && <Loader />}

        {dataFlag === DataFlag.DATA && (
          <>
            {!detail?.preview && bodySection && (
              <VisitList bodySection={{ ...bodySection, sendReport: openResend }} seeDetail={seeDetail} {...visitList} />
            )}

            {!!detail?.preview && detail.visitData && bodySection && (
              <DetailVisit
                saveReportUrl={saveReportUrl}
                bodySection={{ ...bodySection, sendReport: openResend }}
                data={detail.visitData}
                questionsData={questions}
                logbookPrefilters={logbookPrefilters}
                presetDestination={presetDestination ?? undefined}
                createUserId={createUserId}
                hasActivities={hasActivities}
                userQuestionnaireIds={userQuestionnaireIds}
                questionnaireName={questionnaireName}
              />
            )}
          </>
        )}
      </Box>

      {bodySection && (
        <Hidden xlDown>
          <CustomPaper sx={styles.detail} elevation={2}>
            <BosySecton {...bodySection} saveReportUrl={saveReportUrl} sendReport={openResend} />
          </CustomPaper>
        </Hidden>
      )}

      <Resend
        ref={ref}
        isUser
        close={closeModal}
        errorsResend={errorsResend}
        setErrorsResend={setErrorsResend}
        loading={!!modal?.loading}
        open={!!modal?.open}
        send={({ data }) => {
          if (id) submitResendReport({ data, id, questionnaireOption: resend?.queston, type: resend?.type })
        }}
        title={modal?.title ?? ""}
        userGroup={userGroup}
      />

      <SnackSimple close={closeSnackbar} open={openSnackbar.open} type={openSnackbar.type} message={openSnackbar.message} />
    </Box>
  )
}

export default Detail
